import { NavLink, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  LayoutDashboard,
  Calendar,
  CalendarCheck,
  Users,
  Sparkles,
  UserCheck,
  CreditCard,
  BarChart3,
  Megaphone,
  Settings,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@leoclinic/ui';
import type { UserRole } from '@leoclinic/types';
import { getNavItemsForRole } from '@/config/navigation.config';
import { LEO_SUITE } from '@/config/suite.config';
import { AppLogo } from '@/components/branding/app-logo';

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  Calendar,
  CalendarCheck,
  Users,
  Sparkles,
  UserCheck,
  CreditCard,
  BarChart3,
  Megaphone,
  Settings,
};

interface SidebarProps {
  role: UserRole;
}

interface SidebarNavProps {
  role: UserRole;
  onNavigate?: () => void;
}

function SidebarBrand() {
  return (
    <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-6">
      <AppLogo size="sm" />
      <div className="min-w-0">
        <span className="block truncate text-lg font-semibold text-foreground">{LEO_SUITE.productName}</span>
        <span className="block truncate text-[10px] uppercase tracking-wider text-muted-foreground">{LEO_SUITE.suiteName}</span>
      </div>
    </div>
  );
}

function SidebarNav({ role, onNavigate }: SidebarNavProps) {
  const navItems = getNavItemsForRole(role);

  return (
    <nav className="flex-1 space-y-1 overflow-y-auto p-4">
      {navItems.map((item) => {
        const Icon = ICON_MAP[item.icon] ?? LayoutDashboard;
        return (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {item.title}
            {item.badge && (
              <span className="ml-auto rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground">
                {item.badge}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * Sidebar de navegação principal (desktop)
 */
export function Sidebar({ role }: SidebarProps) {
  return (
    <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-card">
      <SidebarBrand />
      <SidebarNav role={role} />
      <div className="shrink-0 border-t border-border p-4">
        <p className="text-xs text-muted-foreground text-center">{LEO_SUITE.suiteName} · v1.0.0</p>
      </div>
    </aside>
  );
}

interface MobileSidebarProps {
  role: UserRole;
  open: boolean;
  onClose: () => void;
}

/**
 * Menu lateral deslizante para telemóvel / PWA
 */
export function MobileSidebar({ role, open, onClose }: MobileSidebarProps) {
  const location = useLocation();

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 md:hidden"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 left-0 z-50 flex w-[min(18rem,85vw)] flex-col border-r border-border bg-card pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
          <div className="flex items-center gap-3 min-w-0">
            <AppLogo size="sm" />
            <div className="min-w-0">
              <span className="block truncate text-lg font-semibold text-foreground">{LEO_SUITE.productName}</span>
              <span className="block truncate text-[10px] uppercase tracking-wider text-muted-foreground">{LEO_SUITE.suiteName}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarNav role={role} onNavigate={onClose} />
        <div className="shrink-0 border-t border-border p-4">
          <p className="text-xs text-muted-foreground text-center">{LEO_SUITE.suiteName} · v1.0.0</p>
        </div>
      </aside>
    </>
  );
}
